/*
  Custom Quick View for Shopify Horizon
  =====================================
  A bespoke modal (shell, layout, animation) that is completely independent of
  Horizon's built-in quick-add, BUT reuses Horizon's real <variant-picker> and
  <product-form-component> so that variant switching and Add to Cart behave
  100% natively:
    - cart drawer opens itself
    - cart bubble refreshes
    - loading / sold-out / error states are handled by the theme
    - NO cart logic is duplicated here

  How it stays native
  -------------------
  We fetch the product PAGE (not just the .js JSON) and lift the theme's own
  interactive custom elements out of it, then move them into our layout. Custom
  elements re-run connectedCallback on insertion, so they wire themselves back
  up and keep communicating with <cart-drawer> through Horizon's document-level
  events. We only render the *presentational* chrome (title, badges, rating,
  description, "view more" link) ourselves.

  Theme-coupling points (edit these if your Horizon version differs):
    1. The SELECTORS map below.
    2. The `@theme/events` import (ThemeEvents). This resolves via the theme's
       importmap declared in theme.liquid — the same one quick-add.js uses.
*/

import { ThemeEvents } from '@theme/events';

// --- Theme-specific hooks — verify against your theme version ---------------
const SELECTORS = {
  gridContent: '[data-product-grid-content]', // wrapper around product info on the PDP
  media: '.product-information__media',        // media gallery
  price: 'product-price',                      // <product-price>
  variantPicker: 'variant-picker',             // <variant-picker>
  quantity: 'quantity-selector, .quantity-selector',
  productForm: 'product-form-component',       // holds the <form> + Add to Cart button
  title: '.view-product-title, h1',
  badges: '.oath-pdp-badges',                  // Oath PDP badges block (top tag + limited stock + rating)
};

class QuickView {
  #modal;
  #dialog;
  #slots = {};
  #cache = new Map();     // handle -> { doc, json }  (data cached, DOM is not)
  #currentHandle = null;
  #lastFocused = null;
  #scrollY = 0;
  #isOpen = false;

  constructor() {
    this.#modal = document.getElementById('custom-quick-view');
    if (!this.#modal) return; // modal snippet not on this page

    this.#dialog = this.#modal.querySelector('.custom-quick-view__dialog');

    // Cache every slot once so we never re-query the DOM per open.
    this.#slots = {
      media:    this.#modal.querySelector('[data-cqv-media]'),
      title:    this.#modal.querySelector('[data-cqv-title]'),
      desc:     this.#modal.querySelector('[data-cqv-desc]'),
      price:    this.#modal.querySelector('[data-cqv-price]'),
      variant:  this.#modal.querySelector('[data-cqv-variant]'),
      actions:  this.#modal.querySelector('[data-cqv-actions]'),
      badges:   this.#modal.querySelector('[data-cqv-badges]'),
      rating:   this.#modal.querySelector('[data-cqv-rating]'),
      viewMore: this.#modal.querySelector('[data-cqv-view-more]'),
    };

    this.bindEvents();
  }

  /* 1. EVENT WIRING ------------------------------------------------------- */
  bindEvents() {
    // OPEN — event-delegated so it also works for cards injected later
    // (collection filtering, infinite scroll, etc.).
    document.addEventListener('click', (event) => {
      const btn = event.target.closest('.quick-view-btn');
      if (!btn) return;
      event.preventDefault();
      const handle = btn.dataset.productHandle;
      if (!handle) return;

      // If the cart drawer is open, close it first, then open the quick view
      // once it has had time to animate out.
      const cartWasOpen = this.#closeCartDrawerIfOpen();
      if (cartWasOpen) {
        setTimeout(() => this.openQuickView(handle), 300);
      } else {
        this.openQuickView(handle);
      }
    });

    // CLOSE — close button or clicking the overlay backdrop.
    this.#modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-cqv-close]') || event.target.closest('[data-cqv-overlay]')) {
        this.closeQuickView();
      }
    });

    // CLOSE — Escape key.
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.#isOpen) this.closeQuickView();
    });

    // Any option change inside the modal -> refresh our custom "view more" link.
    // (Price / image / availability update themselves — they're native nodes.)
    this.#modal.addEventListener('change', () => {
      if (this.#isOpen) this.updateVariant();
    });
    document.addEventListener(ThemeEvents.variantUpdate, () => {
      if (this.#isOpen) this.updateVariant();
    });

    // Native successful Add to Cart -> close quick view. The cart drawer opens
    // itself, so we just get out of the way. We don't touch the cart at all.
    document.addEventListener(ThemeEvents.cartUpdate, (event) => {
      if (this.#isOpen && !event.detail?.data?.didError) this.closeQuickView();
    });
  }

  /* 2. OPEN --------------------------------------------------------------- */
  async openQuickView(handle) {
    this.#currentHandle = handle;
    this.#lastFocused = document.activeElement;
    this.#lockScroll();

    // Show the shell immediately with a loader (perceived performance).
    this.#modal.classList.add('is-open', 'is-loading');
    this.#modal.setAttribute('aria-hidden', 'false');
    this.#isOpen = true;

    try {
      const { doc, json } = await this.loadProduct(handle);
      // A second button may have been clicked while we awaited — bail if so.
      if (this.#currentHandle !== handle) return;
      this.renderProduct(handle, doc, json);
    } catch (error) {
      console.error('[QuickView] load failed:', error);
      if (this.#slots.title) this.#slots.title.textContent = 'Sorry, this product could not be loaded.';
    } finally {
      this.#modal.classList.remove('is-loading');
    }

    this.#dialog.querySelector('[data-cqv-close]')?.focus();
  }

  /* 3. CLOSE -------------------------------------------------------------- */
  closeQuickView() {
    if (!this.#isOpen) return;
    this.#isOpen = false;
    this.#modal.classList.remove('is-open');
    this.#modal.setAttribute('aria-hidden', 'true');

    // Wipe injected content AFTER the close animation. This is important: it
    // stops a hidden <product-form-component> from lingering in the DOM and
    // double-reacting to global cart events.
    const clear = () => {
      for (const key of ['media', 'title', 'desc', 'price', 'variant', 'actions', 'badges', 'rating']) {
        this.#slots[key]?.replaceChildren();
      }
    };
    this.#dialog.addEventListener('transitionend', clear, { once: true });
    setTimeout(clear, 400); // fallback if transitionend never fires

    this.#unlockScroll();
    this.#lastFocused?.focus?.();
  }

  /* 4. DATA LOADING ------------------------------------------------------- */
  // Loads only when needed and caches per handle. Never preloads all products.
  async loadProduct(handle) {
    if (this.#cache.has(handle)) return this.#cache.get(handle);

    // Product PAGE = native components. Product JSON = description / tags /
    // inventory used by our custom badges & metafields later. JSON is optional.
    const [pageRes, jsonRes] = await Promise.all([
      fetch(`/products/${handle}`),
      fetch(`/products/${handle}.js`).catch(() => null),
    ]);
    if (!pageRes.ok) throw new Error(`HTTP ${pageRes.status} fetching product page`);

    const doc = new DOMParser().parseFromString(await pageRes.text(), 'text/html');
    const json = jsonRes && jsonRes.ok ? await jsonRes.json() : null;

    const data = { doc, json };
    this.#cache.set(handle, data);
    return data;
  }

  /* 5. RENDER ------------------------------------------------------------- */
  renderProduct(handle, doc, json) {
    const grid = doc.querySelector(SELECTORS.gridContent);
    if (!grid) throw new Error('Product grid content not found — check SELECTORS.gridContent');

    // Import the PDP subtree into THIS document, held in a DETACHED node so the
    // custom elements don't connect yet (avoids a wasteful connect/disconnect
    // and keeps reflow to a minimum).
    const holder = document.createElement('div');
    holder.appendChild(document.importNode(grid, true));

    // Move (not clone) each interactive piece into our layout. Moving a node
    // from the detached holder into a live slot connects it exactly once, so
    // each custom element initialises a single time.
    const move = (selector, slot) => {
      const node = holder.querySelector(selector);
      if (node && slot) slot.appendChild(node);
      return node;
    };

    // Featured image only — no gallery. We build a single <img> ourselves
    // rather than moving the native media element.
    this.#renderFeaturedImage(json, doc);

    move(SELECTORS.price, this.#slots.price);
    move(SELECTORS.variantPicker, this.#slots.variant);
    move(SELECTORS.quantity, this.#slots.actions);    // quantity (if it's a separate element)
    move(SELECTORS.productForm, this.#slots.actions); // then the native Add to Cart form

    // --- Presentational bits we own -------------------------------------
    this.#slots.title.textContent =
      json?.title || holder.querySelector(SELECTORS.title)?.textContent?.trim() || '';

    this.#renderDescription(json);
    this.#renderBadges(doc);
    this.updateVariant(); // set the initial "view more details" link
  }

  /* 6. VARIANT SYNC — custom bits only; native elements self-update ------- */
  updateVariant() {
    const id = this.#getVariantId();
    const json = this.#cache.get(this.#currentHandle)?.json;

    // "View more details" link.
    if (this.#slots.viewMore) {
      const base = json?.url || `/products/${this.#currentHandle}`;
      this.#slots.viewMore.href = id ? `${base}?variant=${id}` : base;
    }

    // Swap the featured image to the selected variant's image, if it has one.
    if (json && id) {
      const variant = json.variants?.find((v) => String(v.id) === String(id));
      const src = variant?.featured_image?.src;
      const img = this.#slots.media?.querySelector('img');
      if (img && src) img.src = this.#sizedImage(src, 900);
    }
  }

  /* 7. ADD TO CART -------------------------------------------------------- */
  // Not used by default: we keep Horizon's real Add to Cart button in the
  // actions slot and simply restyle it in CSS, so cart behaviour stays 100%
  // native. This helper only exists if you prefer a SEPARATE custom button —
  // wire it to call quickView.addToCart().
  addToCart() {
    const form = this.#slots.actions.querySelector('form');
    if (form?.requestSubmit) form.requestSubmit();
    else form?.querySelector('[type="submit"]')?.click();
  }

  /* --- helpers ----------------------------------------------------------- */
  // Detect whether Horizon's cart drawer is open and, if so, close it using the
  // most native method available. Returns true if it was open. Selectors are
  // defensive because the exact element/state varies by theme version.
  #closeCartDrawerIfOpen() {
    const drawer = document.querySelector(
      'cart-drawer, cart-drawer-component, #cart-drawer, .cart-drawer, .drawer--cart'
    );
    if (!drawer) return false;

    const innerDialog = drawer.querySelector('dialog[open]');
    const isOpen =
      !!innerDialog ||
      drawer.hasAttribute('open') ||
      drawer.classList.contains('is-open') ||
      drawer.classList.contains('active') ||
      drawer.getAttribute('aria-hidden') === 'false';

    if (!isOpen) return false;

    // Prefer the theme's own close method (DialogComponent.closeDialog), then
    // the native <dialog>, then a visible close button.
    if (typeof drawer.closeDialog === 'function') {
      drawer.closeDialog();
    } else if (innerDialog && typeof innerDialog.close === 'function') {
      innerDialog.close();
    } else {
      drawer.querySelector('[ref="closeButton"], .close-button, [aria-label*="close" i]')?.click();
    }
    return true;
  }

  #getVariantId() {
    return (
      this.#slots.actions.querySelector('[name="id"]')?.value ||
      this.#slots.variant.querySelector('input:checked[data-variant-id]')?.dataset.variantId ||
      null
    );
  }

  // Build a single featured <img> in the media slot (no gallery). Falls back
  // to the first PDP media image when the .js JSON is unavailable.
  #renderFeaturedImage(json, doc) {
    const slot = this.#slots.media;
    if (!slot) return;
    slot.replaceChildren();

    const src = json?.featured_image || doc.querySelector(`${SELECTORS.media} img`)?.getAttribute('src');
    if (!src) return;

    const img = document.createElement('img');
    img.className = 'cqv-featured-image';
    img.src = this.#sizedImage(src, 900);
    img.alt = json?.title || '';
    img.loading = 'eager';
    slot.appendChild(img);
  }

  // Request an appropriately sized image from Shopify's CDN. Handles the
  // protocol-relative (//cdn...) URLs the .js endpoint returns.
  #sizedImage(url, width) {
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch {
      return url;
    }
  }

  #renderDescription(json) {
    const slot = this.#slots.desc;
    if (!slot) return;
    if (!json?.description) { slot.hidden = true; return; }
    // Plain-text excerpt only — never inject arbitrary product HTML.
    const tmp = document.createElement('div');
    tmp.innerHTML = json.description;
    const text = (tmp.textContent || '').trim();
    const match = text.match(/^.*?[.](?=\s|$)/); // first period followed by space or end
    slot.textContent = match ? match[0] : text;
    slot.hidden = !text;
  }

  // Lift the real Oath PDP badges block (top tag + limited-stock + rating) out
  // of the fetched product page. This keeps the merchant's metafield value,
  // block settings and the top-tag's inline colours exactly — nothing is
  // reconstructed from JSON. The block already contains the rating, so there's
  // no separate rating render.
  #renderBadges(doc) {
    const slot = this.#slots.badges;
    if (!slot) return;
    slot.replaceChildren();

    const block = doc.querySelector(SELECTORS.badges);
    if (block) slot.appendChild(document.importNode(block, true));
  }

  #lockScroll() {
    this.#scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#scrollY}px`;
    document.body.style.width = '100%';
  }

  #unlockScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this.#scrollY);
  }
}

/* Single instance for the whole site (module scope — no window global). */
let instance = null;
const init = () => { if (!instance) instance = new QuickView(); };
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();